/**
 * Übertragung im Verlauf (Board „Verlauf · Übertragungen“): Zeile und Details nur aus dem Datensatz.
 * Die Stufen Eingereiht · Gesendet · Bestätigt kommen aus den echten Zeitstempeln in Objektzeit.
 */
import type { DynamicPricingPublicationHistoryItem } from '@/shared/api';
import { historyDateTime, historyMoney, historyStayRange } from './historyFormat';
import { PUBLICATION_STATUS, publicationErrorText, type Tone } from './historyText';
import { warningCodeText } from '../pricingCodeText';
import type { HistoryDetail } from './historyDetailModel';

export const publicationStatus = (status: string) =>
  PUBLICATION_STATUS[status] ?? { label: status, tone: 'grey' as Tone };

export function publicationValue(item: DynamicPricingPublicationHistoryItem) {
  const rate = item.rate === null ? null : historyMoney(item.rate, item.currency);
  const stay = item.minimumStay === null ? null : String(item.minimumStay);
  return [rate, stay].filter(Boolean).join(' · ') || '—';
}

export function publicationCells(item: DynamicPricingPublicationHistoryItem) {
  return {
    at: historyDateTime(item.createdAtUtc, item.scope.timezone),
    apartment: item.scope.propertyName,
    range: historyStayRange(item.stayFrom, item.stayTo),
    value: publicationValue(item),
    status: publicationStatus(item.status),
  };
}

function steps(item: DynamicPricingPublicationHistoryItem): { label: string; tone: Tone }[] {
  const failedSend = item.status === 'Failed';
  const failedConfirm = item.status === 'ConfirmationFailed';
  return [
    { label: 'Eingereiht', tone: 'green' },
    { label: 'Gesendet', tone: item.sentAtUtc ? 'green' : failedSend ? 'red' : 'grey' },
    {
      label: 'Bestätigt',
      tone: item.confirmedAtUtc ? 'green' : failedConfirm ? 'red' : 'grey',
    },
  ];
}

export function publicationDetail(item: DynamicPricingPublicationHistoryItem): HistoryDetail {
  const time = (value: string | null) => historyDateTime(value, item.scope.timezone);
  const cells = publicationCells(item);
  const lines = [
    ...(item.errorCode
      ? [{ text: publicationErrorText(item.errorCode), code: item.errorCode, tone: 'red' as Tone }]
      : []),
    ...item.warningCodes.map((code) => ({
      text: warningCodeText(code) ?? 'Hinweis zur Übertragung',
      code,
      tone: 'amber' as Tone,
    })),
  ];
  return {
    eyebrow: `Übertragung · ${cells.at}`,
    title: `${cells.apartment} · ${cells.range}`,
    steps: steps(item),
    facts: [
      ['Übertragener Preis', historyMoney(item.rate, item.currency)],
      ['Mindestnächte', item.minimumStay === null ? '—' : String(item.minimumStay)],
      ['Status', cells.status.label],
      ['Eingereicht', time(item.createdAtUtc)],
      ['Gesendet', time(item.sentAtUtc)],
      ['Bestätigt', time(item.confirmedAtUtc)],
      ['Sendeversuche', String(item.attempts)],
      ['Rückfragen bei Channex', String(item.readbackAttempts)],
      ['Korrekturen', String(item.correctionAttempts)],
    ],
    box: lines.length ? { title: 'Rückmeldung', lines } : undefined,
    refs: [
      ['Übertragung', item.id],
      ['Entscheidung', item.decisionId],
      ['Anfrage', item.requestId],
      ['Einstellungs-Version', item.configurationVersionId],
      ['Strategie-Version', item.strategyProfileVersionId ?? '—'],
    ],
  };
}
